import { AsyncQueue, sleep } from "../async";
import { utf8 } from "../bytes";
import { HermesError } from "../errors";
import { obj, str, type Json, type JsonObject } from "../json";
import type { GatewaySocket, HermesTransport, HttpRequest, HttpResponse } from "../transport";
import {
  cronRunSession, demoCommands, demoCronJobs, demoHubItems, demoProfiles, demoReply, demoSessions, demoSkills,
  demoSkillTree, demoSoul, demoUsage, type DemoReply, type DemoSession,
} from "./content";

/**
 * 体验模式：一个跑在浏览器本地的“假 Hermes”，说和 `hermes serve` 一样的协议（JSON-RPC over WebSocket + 几个 REST 接口），
 * 数据都是写死的示例（对应 iOS 的 DemoGateway）。上层（HermesClient、各个 Store、界面）和连真主机时完全一样。
 */
export class DemoGateway implements HermesTransport {
  readonly engine = new DemoEngine();

  async request(req: HttpRequest): Promise<HttpResponse> {
    const [status, body] = this.engine.http(req.method, req.path);
    return { status, headers: { "content-type": "application/json" }, body: utf8(JSON.stringify(body)) };
  }

  async openSocket(): Promise<GatewaySocket> {
    const socket = new DemoSocket(this.engine);
    this.engine.attach(socket);
    return socket;
  }
}

class DemoSocket implements GatewaySocket {
  private inbound = new AsyncQueue<string>();

  constructor(private readonly engine: DemoEngine) {}

  push(frame: Json): void {
    this.inbound.push(JSON.stringify(frame));
  }

  async send(text: string): Promise<void> {
    let frame: unknown;
    try { frame = JSON.parse(text); } catch { return; }
    this.engine.receive(frame, this);
  }

  receive(): Promise<string> {
    return this.inbound.next();
  }

  close(): void {
    this.inbound.close(HermesError.transport("连接已关闭"));
    this.engine.detach(this);
  }
}

class Rejected extends Error {}

const readOnly = "体验模式里只能看看、聊几句；连接你自己的 Hermes 后就能修改了";

class DemoEngine {
  private socket?: DemoSocket;
  private sessions: DemoSession[] = demoSessions();
  private seq = new Map<string, number>();
  private counter = 0;
  private running = new Map<string, AbortController>();

  attach(socket: DemoSocket): void {
    this.socket = socket;
    socket.push(event("gateway.ready", { replay_epoch: "demo", heartbeat: false }));
  }

  detach(socket: DemoSocket): void {
    if (this.socket === socket) this.socket = undefined;
  }

  // MARK: JSON-RPC

  receive(raw: unknown, socket: DemoSocket): void {
    const frame = obj(raw);
    const method = str(frame?.method);
    if (!frame || !method) return; // 客户端对服务端请求的回答：体验模式不发这类请求
    const id = (frame.id ?? null) as Json;
    let reply: Json;
    try {
      reply = { jsonrpc: "2.0", id, result: this.handle(method, obj(frame.params) ?? {}) };
    } catch (e) {
      reply = e instanceof Rejected
        ? { jsonrpc: "2.0", id, error: { code: 4001, message: e.message } }
        : { jsonrpc: "2.0", id, error: { code: -32603, message: String(e) } };
    }
    // 模拟一点网络延迟，界面上的“加载中”状态也能看到
    if (id !== null) setTimeout(() => socket.push(reply), 40);
  }

  private handle(method: string, p: JsonObject): Json {
    switch (method) {
      case "ping": return { pong: true };
      case "client.capabilities": return {};

      case "profiles.list":
        return { profiles: demoProfiles, bot_mode_protocol: true };
      case "profiles.describe": {
        const name = str(p.name) ?? "default";
        const bot = demoProfiles.find((b) => b.name === name);
        return { name, description: bot?.description ?? null, soul: demoSoul(name), model: { provider: "demo", default: "示例模型" } };
      }
      case "profiles.get_asset":
        return { found: false };

      case "session.list": {
        const profile = str(p.profile) ?? "default";
        const rows = this.sessions.filter((x) => x.profile === profile && x.messages.length > 0)
          .sort((a, b) => b.startedAt - a.startedAt).map(summary);
        return { sessions: rows };
      }
      case "session.create": {
        this.counter += 1;
        const profile = str(p.profile) ?? "default";
        const now = Date.now() / 1000;
        const x: DemoSession = { storedID: `demo-${Math.floor(now)}-${this.counter}`, profile, startedAt: now, messages: [], liveID: `live-${this.counter}` };
        this.sessions.push(x);
        return snapshot(x, true);
      }
      case "session.resume": {
        const stored = str(p.session_id);
        const x = this.sessions.find((y) => y.storedID === stored);
        if (!x) throw new Rejected("找不到这段对话");
        if (!x.liveID) { this.counter += 1; x.liveID = `live-${this.counter}`; }
        return snapshot(x, true);
      }
      case "session.activate": {
        const x = this.live(p.session_id);
        if (!x) throw new Rejected("会话已结束");
        return snapshot(x, p.omit_messages !== true);
      }
      case "session.events.since":
        return { events: [], epoch: "demo", truncated: false };
      case "session.history": {
        const x = this.live(p.session_id);
        return { messages: (x?.messages ?? []) as unknown as Json };
      }
      case "session.title": {
        const x = this.live(p.session_id);
        const title = str(p.title);
        if (x && title) x.title = title;
        return {};
      }
      case "session.delete":
        this.sessions = this.sessions.filter((x) => x.storedID !== str(p.session_id));
        return {};
      case "session.close": {
        const x = this.live(p.session_id);
        if (x) x.liveID = undefined;
        return {};
      }
      case "session.usage":
        return demoUsage;
      case "session.status":
        return { output: "体验模式 · 示例模型 · 数据只保存在这个页面里" };
      case "session.interrupt": {
        const live = str(p.session_id);
        const ctrl = live ? this.running.get(live) : undefined;
        if (live && ctrl) {
          this.running.delete(live);
          ctrl.abort();
          this.emit("message.complete", live, { text: "", status: "interrupted" });
        }
        return { status: "interrupted" };
      }
      case "prompt.submit": {
        const live = str(p.session_id);
        const x = this.live(p.session_id);
        if (!live || !x) throw new Rejected("会话已结束，请新开一段对话");
        const text = str(p.text) ?? "";
        x.messages.push({ role: "user", text, timestamp: Date.now() / 1000 });
        if (!x.title) x.startedAt = Date.now() / 1000;
        const ctrl = new AbortController();
        this.running.set(live, ctrl);
        void this.play(demoReply(text), live, ctrl.signal);
        return { status: "streaming" };
      }
      case "session.steer":
        return { status: "queued" };

      case "cron.manage":
        if (str(p.action) === "list") return { success: true, jobs: demoCronJobs() };
        throw new Rejected(readOnly);
      case "skills.manage":
        switch (str(p.action)) {
          case "list": return { skills: demoSkillTree };
          case "browse": case "search": return { items: demoHubItems, page: 1, total_pages: 1 };
          default: throw new Rejected(readOnly);
        }
      case "model.options":
        return {
          providers: [{ slug: "demo", name: "示例模型服务", models: ["示例模型", "示例模型-快速"], is_current: true, authenticated: true }],
          model: "示例模型", provider: "demo",
        };
      case "commands.catalog":
        return { categories: [{ name: "常用", pairs: demoCommands }], skill_count: 6 };
      case "insights.get":
        return { days: p.days ?? 30, sessions: this.sessions.length, messages: 42 };
      case "mcp.servers.list":
        return { servers: [] };

      case "profiles.create": case "profiles.configure": case "model.save_key": case "model.disconnect": case "config.set":
      case "mcp.servers.remove": case "mcp.servers.test": case "reload.mcp": case "image.attach_bytes": case "file.attach":
      case "prompt.background": case "prompt.btw": case "session.branch": case "session.undo": case "session.compress":
        throw new Rejected(readOnly);
      case "command.dispatch": case "slash.exec":
        return { type: "exec", output: "体验模式里不能执行斜杠命令。连接你自己的 Hermes 后，输入 / 可以用到它的全部命令和技能。" };
      default:
        // 其它只读接口给空结果，界面显示空状态而不是报错
        return {};
    }
  }

  // MARK: 流式回复

  private async play(reply: DemoReply, live: string, signal: AbortSignal): Promise<void> {
    try {
      this.emit("message.start", live, {});
      await sleep(350, signal);
      if (reply.thinking) {
        for (const chunk of chunks(reply.thinking, 8)) {
          this.emit("reasoning.delta", live, { text: chunk });
          await sleep(30, signal);
        }
      }
      if (reply.tool) {
        const toolID = `demo-tool-${this.counter}`;
        this.emit("tool.start", live, { tool_id: toolID, name: reply.tool.name, context: reply.tool.context });
        await sleep(1100, signal);
        this.emit("tool.complete", live, { tool_id: toolID, name: reply.tool.name, summary: reply.tool.summary, duration_s: 1.1 });
        await sleep(200, signal);
      }
      for (const chunk of chunks(reply.text, 6)) {
        this.emit("message.delta", live, { text: chunk });
        await sleep(28, signal);
      }
    } catch {
      return; // 被打断：interrupt 已经发了 message.complete
    }
    this.running.delete(live);
    const x = this.sessions.find((y) => y.liveID === live);
    if (!x) return;
    x.messages.push({ role: "assistant", text: reply.text, timestamp: Date.now() / 1000 });
    this.emit("session.usage", live, { usage: demoUsage });
    this.emit("message.complete", live, { text: reply.text, status: "complete" });
    if (!x.title) {
      x.title = reply.title;
      this.emit("session.title", live, { session_id: x.storedID, title: reply.title });
    }
    this.socket?.push(event("sessions.changed", {}));
  }

  private emit(type: string, live: string, payload: Json): void {
    const next = (this.seq.get(live) ?? 0) + 1;
    this.seq.set(live, next);
    this.socket?.push(event(type, payload, live, next));
  }

  private live(id: Json | undefined): DemoSession | undefined {
    const live = str(id);
    return live ? this.sessions.find((x) => x.liveID === live) : undefined;
  }

  // MARK: REST

  http(method: string, fullPath: string): [number, Json] {
    const path = fullPath.split("?")[0];
    if (method === "GET") {
      if (path === "/api/status") {
        return [200, { version: "体验模式", auth_required: false, auth_providers: [], auth_flows: [], gateway_running: true, gateway_state: "running", overall: "ok" }];
      }
      if (path === "/api/skills") return [200, demoSkills];
      if (path === "/api/skills/content") {
        return [200, { content: "# 示例技能\n\n体验模式里的技能只是示例。连接你自己的 Hermes 后，这里会显示技能的完整说明。" }];
      }
      if (path === "/api/sessions" && fullPath.includes("source=cron")) {
        const run = this.sessions.find((x) => x.storedID === cronRunSession);
        const at = new Date((run?.startedAt ?? 0) * 1000);
        const stamp = `${at.toLocaleString("en-US", { month: "short" })} ${at.getDate()} ${String(at.getHours()).padStart(2, "0")}:${String(at.getMinutes()).padStart(2, "0")}`;
        return [200, { sessions: run ? [{ id: run.storedID, title: `早间新闻摘要 · ${stamp}`, started_at: run.startedAt, last_active: run.startedAt + 48, is_active: false, tool_call_count: 1 }] : [] }];
      }
      if (path === "/api/sessions/search") {
        const hits = this.sessions.filter((x) => x.messages.length > 0).map((x) => ({
          session_id: x.storedID, title: x.title ?? null, source: "timibot", last_active: x.startedAt, session_started: x.startedAt,
        }));
        return [200, { results: hits }];
      }
      if (path.startsWith("/api/sessions/") && path.endsWith("/messages")) {
        const id = path.slice("/api/sessions/".length, -"/messages".length);
        const x = this.sessions.find((y) => y.storedID === id);
        const rows = (x?.messages ?? []).map((m) => ({ role: m.role, content: m.text ?? "", timestamp: m.timestamp ?? 0 }));
        return [200, { messages: rows }];
      }
      if (path.startsWith("/api/cron/jobs/") && path.endsWith("/runs")) {
        const run = this.sessions.find((x) => x.storedID === cronRunSession);
        return [200, run ? [{ id: run.storedID, title: run.title ?? "", started_at: run.startedAt, ended_at: run.startedAt + 48, message_count: run.messages.length }] : []];
      }
      if (path === "/api/mcp/catalog") return [200, { entries: [] }];
      return [404, { detail: "体验模式里没有这项数据" }];
    }
    return [403, { detail: readOnly }];
  }
}

function event(type: string, payload: Json, sessionID?: string, seq?: number): Json {
  const params: JsonObject = { type, payload };
  if (sessionID) params.session_id = sessionID;
  if (seq !== undefined) params.seq = seq;
  return { jsonrpc: "2.0", method: "event", params };
}

function chunks(text: string, size: number): string[] {
  const out: string[] = [];
  let current = "";
  for (const ch of text) {
    current += ch;
    if ([...current].length >= size || ch === "\n") { out.push(current); current = ""; }
  }
  if (current) out.push(current);
  return out;
}

function snapshot(x: DemoSession, includeMessages: boolean): Json {
  const info: JsonObject = {
    model: "示例模型", provider: "demo", running: false, stored_session_id: x.storedID, profile_name: x.profile,
    version: "体验模式", usage: demoUsage,
  };
  if (x.title) info.title = x.title;
  const out: JsonObject = { session_id: x.liveID ?? "", stored_session_id: x.storedID, message_count: x.messages.length, info };
  if (x.title) out.title = x.title;
  if (includeMessages) out.messages = x.messages as unknown as Json;
  return out;
}

function summary(x: DemoSession): Json {
  const preview = x.messages.find((m) => m.role === "user")?.text ?? "";
  const row: JsonObject = { id: x.storedID, preview: [...preview].slice(0, 80).join(""), started_at: x.startedAt, message_count: x.messages.length, source: "timibot" };
  if (x.title) row.title = x.title;
  return row;
}

export const demoGateway = new DemoGateway();

